import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Honeypot from "@/components/Honeypot";
import { Disc3, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full bg-ink border border-stroke px-4 py-3 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid transition-colors";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loadedAtMs] = useState(() => Date.now());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(name, email, password, { website, loaded_at_ms: loadedAtMs });
    setLoading(false);
    if (res.ok) {
      toast.success("Vault created");
      navigate("/app");
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Disc3 className="text-acid" size={30} />
          <span className="font-display text-4xl text-paper">POSTERVAULT</span>
        </Link>
        <div className="bg-surface/85 backdrop-blur-md border border-stroke p-8 hard-shadow">
          <h1 className="font-display text-4xl text-paper mb-1">Create Vault</h1>
          <p className="font-mono text-xs text-muted-gray mb-6">Start your favorite poster portfolio</p>
          <form onSubmit={submit} className="space-y-4">
            <Honeypot value={website} onChange={setWebsite} />
            <input data-testid="register-name-input" required placeholder="NAME" className={inputCls}
              value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="register-email-input" type="email" required placeholder="EMAIL" className={inputCls}
              value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="register-password-input" type="password" required placeholder="PASSWORD (min 6)" className={inputCls}
              value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && <div data-testid="register-error" className="font-mono text-xs text-magenta border border-magenta/40 bg-magenta/10 px-3 py-2">{error}</div>}
            <button type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full bg-acid text-ink font-bold font-mono text-sm uppercase tracking-wider py-3.5 hover:bg-glow disabled:opacity-50 flex items-center justify-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />} Create Account
            </button>
          </form>
          <p className="font-mono text-xs text-muted-gray mt-6 text-center">
            Have an account? <Link to="/login" className="text-acid hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
